// nuolino-admin/src/components/ui/Toast.tsx
// Sistema de toasts leve: provider + viewport.
// Auto-dismiss, fila, atalho de teclado (Esc limpa), aria-live polite.
// Tipos em ./toast-types; hook em ./use-toast.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { CheckCircle2, Info, AlertTriangle, AlertCircle, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Button } from './Button';
import { ToastContext } from './ToastContext';
import type { Toast, ToastContextValue, ToastInput, ToastTone } from './toast-types';

let counter = 0;
function nextId(): string {
  counter += 1;
  return `t-${Date.now().toString(36)}-${counter.toString(36)}`;
}

const toneClasses: Record<ToastTone, { bg: string; fg: string; icon: ReactNode }> = {
  success: {
    bg: 'bg-success-50 border-success-500/30',
    fg: 'text-success-700',
    icon: <CheckCircle2 className="h-5 w-5" aria-hidden="true" />,
  },
  info: {
    bg: 'bg-info-50 border-info-500/30',
    fg: 'text-info-700',
    icon: <Info className="h-5 w-5" aria-hidden="true" />,
  },
  warning: {
    bg: 'bg-warning-50 border-warning-500/30',
    fg: 'text-warning-700',
    icon: <AlertTriangle className="h-5 w-5" aria-hidden="true" />,
  },
  danger: {
    bg: 'bg-danger-50 border-danger-500/30',
    fg: 'text-danger-700',
    icon: <AlertCircle className="h-5 w-5" aria-hidden="true" />,
  },
};

export interface ToastProviderProps {
  children: ReactNode;
  /** Default duration in ms. Default 5000. */
  defaultDurationMs?: number;
}

export function ToastProvider({ children, defaultDurationMs = 5_000 }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: string): void => {
    setToasts((current) => current.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const toast = useCallback(
    (input: ToastInput): string => {
      const id = nextId();
      const next: Toast = {
        id,
        title: input.title,
        description: input.description,
        tone: input.tone ?? 'info',
        durationMs: input.durationMs ?? defaultDurationMs,
      };
      setToasts((current) => [...current, next]);
      if (next.durationMs && next.durationMs > 0) {
        const timer = setTimeout(() => dismiss(id), next.durationMs);
        timers.current.set(id, timer);
      }
      return id;
    },
    [defaultDurationMs, dismiss],
  );

  const clear = useCallback((): void => {
    setToasts([]);
    timers.current.forEach((t) => clearTimeout(t));
    timers.current.clear();
  }, []);

  useEffect(() => {
    const map = timers.current;
    return () => {
      map.forEach((t) => clearTimeout(t));
      map.clear();
    };
  }, []);

  const value = useMemo<ToastContextValue>(() => ({ toast, dismiss, clear }), [toast, dismiss, clear]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

interface ToastViewportProps {
  toasts: Toast[];
  onDismiss: (id: string) => void;
}

function ToastViewport({ toasts, onDismiss }: ToastViewportProps) {
  if (toasts.length === 0) return null;
  return (
    <div
      role="region"
      aria-label="Notificações"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-toast flex flex-col items-center gap-2 px-4 pb-4 sm:right-4 sm:left-auto sm:items-end sm:px-0 sm:pr-4"
    >
      <ol aria-live="polite" aria-relevant="additions" className="flex w-full flex-col gap-2 sm:max-w-sm">
        {toasts.map((t) => {
          const tClass = toneClasses[t.tone];
          return (
            <li
              key={t.id}
              className={cn(
                'pointer-events-auto flex items-start gap-3 rounded-lg border bg-surface px-4 py-3 shadow-level2',
                tClass.bg,
              )}
            >
              <span className={cn('mt-0.5', tClass.fg)}>{tClass.icon}</span>
              <div className="flex-1 min-w-0">
                <p className={cn('text-sm font-semibold', tClass.fg)}>{t.title}</p>
                {t.description && <p className={cn('mt-0.5 text-sm', tClass.fg, 'opacity-90')}>{t.description}</p>}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDismiss(t.id)}
                aria-label="Dispensar notificação"
                leftIcon={<X className="h-4 w-4" />}
              >
                <span className="sr-only">Dispensar</span>
              </Button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
